import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry for error tracking
 * Configures based on the current environment
 */
export const initSentry = () => {
  const currentEnv = (import.meta.env.VITE_ENV || 'local').toLowerCase();
  const allowedEnvs = ['dev', 'prod'];

  // Grouped console log for better visibility
  console.group("📡 Sentry Init Debug");
  console.log("ENV:", currentEnv);
  console.log("DSN:", import.meta.env.VITE_SENTRY_DSN);
  console.groupEnd();

  // Check if environment is allowed
  if (!allowedEnvs.includes(currentEnv)) {
    console.warn(`[Sentry] Skipping init — ENV="${currentEnv}" not in ${allowedEnvs}`);
    return;
  }

  // Check if DSN is provided
  if (!import.meta.env.VITE_SENTRY_DSN) {
    console.warn("Sentry DSN is missing! Error tracking will not work.");
    return; // Don't initialize without DSN
  }

  const allowUrlsMap = {
    prod: ['https://jobpool.ai', 'https://www.jobpool.ai'],
    dev: ['https://dev.jobpool.ai']
  };

  try {
    // Initialize Sentry with appropriate configuration
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: currentEnv,
      tracesSampleRate: 1.0,
      debug: currentEnv === 'dev',
      beforeSend: (event) => {
        console.log("Sentry sending event:", event);
        return event;
      },
      allowUrls: allowUrlsMap[currentEnv] || undefined,
    });

    console.log("Sentry initialized successfully!");

    // Send a test error immediately to verify setup
    try {
      const testId = Math.random().toString(36).substring(2, 8);
      throw new Error(`Sentry Init Test Error (ID: ${testId})`);
    } catch (e) {
      console.log("Sending init test error to Sentry:", e.message);
      Sentry.captureException(e, {
        tags: {
          testType: 'init',
          location: 'sentry.js',
          initPhase: 'startup'
        },
        level: 'info'
      });
    }
  } catch (error) {
    console.error("Failed to initialize Sentry:", error);
  }
};

/**
 * Helper function to capture exceptions with standard context
 */
export const captureError = (error, additionalContext = {}) => {
  console.log("captureError called with:", error.message);

  try {
    Sentry.withScope(scope => {
      // Add standard tags
      scope.setTag('appVersion', import.meta.env.VITE_APP_VERSION || 'unknown');

      // Add any additional tags
      if (additionalContext.tags) {
        Object.entries(additionalContext.tags).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }

      // Add any additional context
      if (additionalContext.extra) {
        Object.entries(additionalContext.extra).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }

      // Set error level if provided
      if (additionalContext.level) {
        scope.setLevel(additionalContext.level);
      }

      // Capture the exception
      console.log("About to call Sentry.captureException");
      Sentry.captureException(error);
      console.log("Sentry.captureException called successfully");
    });
  } catch (e) {
    console.error("Error in captureError:", e);
  }
};

export default {
  initSentry,
  captureError
};